import React, { useState, useEffect } from "react";
import { useAuth } from "../App";
import axios from "axios";
import {
  Users, FileText, QrCode, ExternalLink, Copy, Check,
  Download, Eye, Building2, Phone, Mail, Clock, Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Layout from "../components/Layout";

import { API } from "../config/api";

export default function CPSubmissionsPage() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("submissions"); // submissions | tokens
  const [typeFilter, setTypeFilter] = useState("all");
  const [copiedLink, setCopiedLink] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsRes, statsRes, tokensRes] = await Promise.all([
        axios.get(`${API}/cp-portal/submissions`),
        axios.get(`${API}/cp-portal/submissions/stats`),
        axios.get(`${API}/cp-portal/tokens`),
      ]);
      setSubmissions(subsRes.data);
      setStats(statsRes.data);
      setTokens(tokensRes.data);
    } catch (e) {
      console.error("Failed to fetch CP data:", e);
    } finally {
      setLoading(false);
    }
  };

  const copyPartnerLink = (code) => {
    const url = `${window.location.origin}/cp/${code}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(code);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const filtered = typeFilter === "all" ? submissions : submissions.filter(s => s.type === typeFilter);

  const formatDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="cp-submissions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Channel Partner Portal</h1>
            <p className="text-sm text-slate-500 mt-1">Partner submissions, tokens & management</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="cp-stats-grid">
            <StatCard label="Total Submissions" value={stats.total_submissions} color="blue" />
            <StatCard label="Lead Submissions" value={stats.lead_submissions} color="sky" />
            <StatCard label="EOI Submissions" value={stats.eoi_submissions} color="amber" />
            <StatCard label="Token Amount" value={`Rs.${(stats.total_token_amount || 0).toLocaleString()}`} color="green" />
            <StatCard label="Active Partners" value={stats.active_partners} color="purple" />
            <StatCard label="Total Partners" value={stats.total_partners} color="slate" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit mb-4">
          <button
            data-testid="cp-tab-submissions"
            onClick={() => setTab("submissions")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === "submissions" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            Submissions
          </button>
          <button
            data-testid="cp-tab-tokens"
            onClick={() => setTab("tokens")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === "tokens" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            Digital Tokens
          </button>
        </div>

        {/* Submissions Tab */}
        {tab === "submissions" && (
          <Card className="border-slate-200" data-testid="cp-submissions-table">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Partner Submissions</CardTitle>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <select
                    data-testid="cp-type-filter"
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    className="text-sm border border-slate-200 rounded-md px-2 py-1"
                  >
                    <option value="all">All Types</option>
                    <option value="lead">Leads Only</option>
                    <option value="eoi">EOIs Only</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-slate-400">Loading...</div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-slate-400">No submissions yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-y border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Type</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Customer</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Project</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Partner</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Amount</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Token</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Date</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filtered.map(sub => (
                        <tr key={sub.submission_id} className="hover:bg-slate-50" data-testid={`cp-submission-${sub.submission_id}`}>
                          <td className="px-4 py-3">
                            <Badge className={sub.type === "eoi" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200"}>
                              {sub.type === "eoi" ? "EOI" : "Lead"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">{sub.customer_name}</p>
                            <p className="text-xs text-slate-400">{sub.customer_phone}</p>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{sub.project_name || sub.project_interest || "-"}</td>
                          <td className="px-4 py-3">
                            <span className="text-slate-600">{sub.partner_name}</span>
                            <button
                              onClick={() => copyPartnerLink(sub.partner_code)}
                              className="ml-2 text-slate-400 hover:text-slate-600 inline"
                              title="Copy partner link"
                            >
                              {copiedLink === sub.partner_code ? <Check className="w-3 h-3 inline text-green-500" /> : <Copy className="w-3 h-3 inline" />}
                            </button>
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {sub.token_amount ? `Rs.${Number(sub.token_amount).toLocaleString()}` : "-"}
                          </td>
                          <td className="px-4 py-3">
                            {sub.digital_token ? (
                              <button
                                onClick={() => setSelectedToken(sub.digital_token)}
                                className="text-amber-600 hover:text-amber-700 font-mono text-xs"
                              >
                                {sub.digital_token.token_code}
                              </button>
                            ) : "-"}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-400">{formatDate(sub.created_at)}</td>
                          <td className="px-4 py-3">
                            {sub.cheque_url && (
                              <a href={sub.cheque_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 text-xs">
                                <Eye className="w-3 h-3 inline mr-1" />Cheque
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tokens Tab */}
        {tab === "tokens" && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="cp-tokens-grid">
            {loading ? (
              <div className="col-span-full text-center text-slate-400 py-8">Loading...</div>
            ) : tokens.length === 0 ? (
              <div className="col-span-full text-center text-slate-400 py-8">No digital tokens generated yet</div>
            ) : (
              tokens.map(token => (
                <Card key={token.token_id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`cp-token-${token.token_code}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200 font-mono">{token.token_code}</Badge>
                      <Badge className="bg-green-50 text-green-700 border-green-200">Priority #{token.priority_number}</Badge>
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <p className="font-medium text-slate-900">{token.customer_name}</p>
                      <p className="text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{token.customer_phone}</p>
                      <p className="text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3" />{token.project_name}</p>
                      <p className="text-slate-500">Rs. {Number(token.token_amount).toLocaleString()}</p>
                      <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(token.created_at)}</p>
                    </div>
                    {token.token_image_url && (
                      <a
                        href={token.token_image_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-700 font-medium"
                        data-testid={`cp-view-token-${token.token_code}`}
                      >
                        <QrCode className="w-3 h-3" /> View Token Image
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Token Detail Modal */}
        {selectedToken && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedToken(null)} data-testid="cp-token-modal">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <QrCode className="w-5 h-5 text-amber-500" /> Digital Token
              </h3>
              <div className="space-y-3 text-sm">
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-amber-600 uppercase tracking-wider">Token Code</p>
                  <p className="text-2xl font-bold font-mono text-amber-700">{selectedToken.token_code}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-slate-400 text-xs">Customer</p><p className="font-medium">{selectedToken.customer_name}</p></div>
                  <div><p className="text-slate-400 text-xs">Phone</p><p className="font-medium">{selectedToken.customer_phone}</p></div>
                  <div><p className="text-slate-400 text-xs">Project</p><p className="font-medium">{selectedToken.project_name}</p></div>
                  <div><p className="text-slate-400 text-xs">Priority</p><p className="font-medium">#{selectedToken.priority_number}</p></div>
                  <div><p className="text-slate-400 text-xs">Amount</p><p className="font-medium">Rs. {Number(selectedToken.token_amount).toLocaleString()}</p></div>
                  <div><p className="text-slate-400 text-xs">Partner</p><p className="font-medium">{selectedToken.partner_name}</p></div>
                </div>
                {selectedToken.token_image_url && (
                  <div className="text-center mt-4">
                    <img src={selectedToken.token_image_url} alt="Token" className="max-w-full rounded-lg shadow-md mx-auto" />
                    <a href={selectedToken.token_image_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-amber-600 hover:text-amber-700 text-sm font-medium">
                      <Download className="w-4 h-4" /> Download
                    </a>
                  </div>
                )}
              </div>
              <Button onClick={() => setSelectedToken(null)} variant="outline" className="w-full mt-4">Close</Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-700",
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700",
    green: "bg-green-50 text-green-700",
    purple: "bg-purple-50 text-purple-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className={`rounded-xl p-3 ${colors[color] || colors.slate}`} data-testid={`cp-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-xs opacity-70">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}
